/**
 * Setter opp globale objekter som server side rendering (SSR) er avhengig av.
 *
 * SSR kjører i ClearScript V8 via ReactJS.NET. Den motoren har verken `window`, `self` eller `global` – kun
 * `globalThis`. Flere avhengigheter (bl.a. TextEncoder-polyfillen som `react-dom/server` bruker) slår opp det
 * globale objektet via nettopp disse navnene. Med Vite 7 (Rollup) ble det lagt inn en `globalThis`-shim
 * automatisk, men Vite 8 (Rolldown) gjør ikke det lenger.
 *
 * I tillegg laster ReactJS.NET sin egen `shims.js` før våre bundler, og den kjører `var global = global || {}`.
 * `global` finnes dermed, men er en *tom stubb* som ikke er det globale objektet. Polyfiller som skriver til
 * `global` havner da i tomrommet, og globalene (`TextEncoder`, `React`, …) blir aldri tilgjengelige. Resultatet
 * var at `common.js` kræsjet med «TextEncoder is not defined», `globalThis.React` aldri ble satt, og
 * `helsenorge.js` deretter feilet med «Cannot read properties of undefined (reading 'Component')».
 * Derfor peker vi `global` til det virkelige globale objektet.
 *
 * Modulen er en no-op i nettleseren, og må importeres **først** i entrypunktene (`common.ts` og `index.ts`) slik
 * at den kjører før noen annen modulkode.
 */
const globalObject = globalThis as unknown as Record<string, unknown>;

// I nettleseren finnes alt dette fra før – da skal vi ikke røre noe.
if (typeof globalObject.document === 'undefined') {
  // Noen biblioteker sjekker `window` for å avgjøre om de kjører i nettleser. Et tomt objekt holder for SSR.
  if (typeof globalObject.window === 'undefined') {
    globalObject.window = {};
  }

  // `self` brukes som alias for det globale objektet (typisk i worker-kompatibel kode).
  if (typeof globalObject.self === 'undefined') {
    globalObject.self = globalObject;
  }

  // `global` kan allerede finnes som en tom stubb fra ReactJS.NET sin shims.js – overstyr den.
  if (globalObject.global !== globalObject) {
    globalObject.global = globalObject;
  }
}

export {};
